import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, Button, Select, Table, StatusBadge, Modal, Textarea } from '@/components/ui';
import {
  Briefcase,
  Mail,
  Phone,
  Calendar,
  Download,
  Eye,
  Clock,
} from 'lucide-react';
import { format } from 'date-fns';

interface JobApplication {
  id: string;
  name: string;
  email: string;
  phone?: string;
  position: string;
  resumeUrl?: string;
  availability: string[];
  status: string;
  notes?: string;
  createdAt: string;
}

const statusOptions = [
  { value: '', label: 'All Statuses' },
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'interviewed', label: 'Interviewed' },
  { value: 'hired', label: 'Hired' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'withdrawn', label: 'Withdrawn' },
];

const statusWorkflow = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'interviewed', label: 'Interviewed' },
  { value: 'hired', label: 'Hired' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'withdrawn', label: 'Withdrawn' },
];

export function JobsPage() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedApplication, setSelectedApplication] = useState<JobApplication | null>(null);

  const { data: applications, isLoading } = useQuery<JobApplication[]>({
    queryKey: ['job-applications', statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      const response = await api.get(`/admin/jobs/applications?${params}`);
      return response.data?.data?.items || response.data?.data || [];
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status, notes }: { id: string; status: string; notes?: string }) => {
      await api.put(`/admin/jobs/${id}/status`, { status, notes });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-applications'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
    },
  });

  const columns = [
    {
      key: 'name',
      header: 'Applicant',
      render: (app: JobApplication) => (
        <div>
          <p className="font-medium text-white">{app.name}</p>
          <p className="text-sm text-gray-500">{app.email}</p>
        </div>
      ),
    },
    {
      key: 'position',
      header: 'Position',
      render: (app: JobApplication) => (
        <span className="text-gray-300">{app.position}</span>
      ),
    },
    {
      key: 'availability',
      header: 'Availability',
      render: (app: JobApplication) => (
        <div className="flex flex-wrap gap-1">
          {app.availability.slice(0, 2).map((day) => (
            <span
              key={day}
              className="px-2 py-0.5 text-xs rounded bg-midnight-100 text-gray-400"
            >
              {day}
            </span>
          ))}
          {app.availability.length > 2 && (
            <span className="px-2 py-0.5 text-xs rounded bg-midnight-100 text-gray-400">
              +{app.availability.length - 2}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (app: JobApplication) => <StatusBadge status={app.status} />,
    },
    {
      key: 'date',
      header: 'Applied',
      render: (app: JobApplication) => (
        <span className="text-gray-400">
          {format(new Date(app.createdAt), 'MMM d, yyyy')}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-24',
      render: (app: JobApplication) => (
        <div className="flex items-center gap-2">
          {app.resumeUrl && (
            <a
              href={app.resumeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-midnight-100"
              onClick={(e) => e.stopPropagation()}
            >
              <Download className="h-4 w-4" />
            </a>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedApplication(app);
            }}
          >
            <Eye className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Job Applications</h1>
          <p className="text-gray-400 mt-1">Manage employment applications</p>
        </div>
      </div>

      {/* Filters */}
      <Card padding="md">
        <div className="flex items-center gap-4">
          <Briefcase className="h-5 w-5 text-gray-400" />
          <div className="w-48">
            <Select
              options={statusOptions}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            />
          </div>
          <div className="ml-auto text-sm text-gray-400">
            {applications?.length ?? 0} applications
          </div>
        </div>
      </Card>

      {/* Applications Table */}
      <Card padding="none">
        <Table
          columns={columns}
          data={applications ?? []}
          keyField="id"
          loading={isLoading}
          onRowClick={setSelectedApplication}
          emptyMessage="No applications found"
        />
      </Card>

      {/* Detail Modal */}
      <ApplicationDetailModal
        application={selectedApplication}
        onClose={() => setSelectedApplication(null)}
        onUpdateStatus={(status, notes) => {
          if (selectedApplication) {
            updateStatusMutation.mutate(
              { id: selectedApplication.id, status, notes },
              { onSuccess: () => setSelectedApplication(null) }
            );
          }
        }}
        isUpdating={updateStatusMutation.isPending}
      />
    </div>
  );
}

// Detail Modal
interface ApplicationDetailModalProps {
  application: JobApplication | null;
  onClose: () => void;
  onUpdateStatus: (status: string, notes?: string) => void;
  isUpdating: boolean;
}

function ApplicationDetailModal({
  application,
  onClose,
  onUpdateStatus,
  isUpdating,
}: ApplicationDetailModalProps) {
  const [notes, setNotes] = useState('');
  const [newStatus, setNewStatus] = useState('');

  useEffect(() => {
    if (application) {
      setNotes(application.notes || '');
      setNewStatus(application.status);
    }
  }, [application]);

  if (!application) return null;

  return (
    <Modal isOpen={!!application} onClose={onClose} title="Application Details" size="lg">
      <div className="space-y-6">
        {/* Applicant Info */}
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 rounded-full bg-midnight-100 flex items-center justify-center">
            <span className="text-2xl font-bold text-gold">
              {application.name.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-white">{application.name}</h3>
            <p className="text-gray-400">Applied for: {application.position}</p>
          </div>
        </div>

        {/* Contact Info */}
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-midnight-100">
              <Mail className="h-4 w-4 text-gray-400" />
            </div>
            <div>
              <p className="text-sm text-gray-400">Email</p>
              <a href={`mailto:${application.email}`} className="text-amber-400 hover:underline">
                {application.email}
              </a>
            </div>
          </div>
          {application.phone && (
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-midnight-100">
                <Phone className="h-4 w-4 text-gray-400" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Phone</p>
                <a href={`tel:${application.phone}`} className="text-amber-400 hover:underline">
                  {application.phone}
                </a>
              </div>
            </div>
          )}
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-midnight-100">
              <Calendar className="h-4 w-4 text-gray-400" />
            </div>
            <div>
              <p className="text-sm text-gray-400">Applied</p>
              <p className="text-white">
                {format(new Date(application.createdAt), 'MMMM d, yyyy')}
              </p>
            </div>
          </div>
        </div>

        {/* Availability */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Clock className="h-4 w-4 text-gray-400" />
            <p className="text-sm text-gray-400">Availability</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {application.availability.map((day) => (
              <span
                key={day}
                className="px-3 py-1 text-sm rounded-lg bg-midnight-100 text-gray-300"
              >
                {day}
              </span>
            ))}
          </div>
        </div>

        {/* Resume */}
        {application.resumeUrl && (
          <div>
            <p className="text-sm text-gray-400 mb-2">Resume</p>
            <a
              href={application.resumeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-midnight-100 text-amber-400 hover:bg-midnight-100/80 transition-colors"
            >
              <Download className="h-4 w-4" />
              Download Resume
            </a>
          </div>
        )}

        {/* Status & Notes */}
        <div className="pt-4 border-t border-midnight-100 space-y-4">
          <Select
            label="Status"
            options={statusWorkflow}
            value={newStatus}
            onChange={(e) => setNewStatus(e.target.value)}
          />
          <Textarea
            label="Internal Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Add notes about this applicant..."
          />
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-midnight-100">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => onUpdateStatus(newStatus, notes)}
            loading={isUpdating}
            disabled={newStatus === application.status && notes === (application.notes || '')}
          >
            Update
          </Button>
        </div>
      </div>
    </Modal>
  );
}
